import { __decorate, __metadata } from "tslib";
import { WidgetBase } from '@dojo/framework/core/WidgetBase';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import isNumber from 'lodash-es/isNumber';
import { logQuiz } from 'logger';
import { LinkedList } from 'collections';
import { setCursor, setQuestion, incrCursor, decrCursor, setInreview, setHasQueue } from './state';
import timesync from 'timesync';
import { QueueLoader, allqueues } from './queueloader';
import css from '../css/questionfeedback.m.css';
import { recordProgress } from './xapi';
const ISCOMPLETE = 1;
const dummyList = new LinkedList();
export class QuestionList extends WidgetBase {
    cursors = [];
    loc = 0;
    inreview = false;
    instanceId;
    originalId;
    detaching = false;
    hasReviewed = false;
    _propsInited = false;
    get loader() {
        const { instanceId, detaching } = this;
        const { svc, quizstore, info: { numberofquestions, lastresult }, isaudit, resultid } = this.properties;
        if (instanceId !== undefined) {
            if (allqueues.has(instanceId)) {
                return allqueues.get(instanceId);
            }
            if (!detaching) {
                const qloader = new QueueLoader({
                    svc,
                    quizstore,
                    numberofquestions: isaudit ? lastresult.numberofquestions : numberofquestions,
                    instanceId,
                    useQueue: false,
                    resultid
                });
                allqueues.set(instanceId, qloader);
                return qloader;
            }
        }
        return undefined;
    }
    get queue() {
        const { loader } = this;
        return loader && loader.queue;
    }
    stopLoading() {
        logQuiz.debug(() => `QuestionList#stopLoading for ${this.instanceId}`);
        const qloader = this.loader;
        if (qloader === undefined || qloader.stoppedloading) {
            return;
        }
        qloader.stopLoading();
    }
    onDetach() {
        logQuiz.debug(() => `QuestionQueue#onDetach for ${this.originalId}`);
        this.detaching = true;
        const qloader = this.loader;
        const { instanceId } = this;
        qloader && qloader.stopLoading();
        instanceId && allqueues.delete(instanceId);
    }
    get_question_array() {
        const { queue } = this;
        if (queue) {
            return queue.toArray();
        }
        return [];
    }
    has_next_question() {
        if (this.cursors.length > 0) {
            return this.loc + 1 < this.cursors.length;
        }
        const { quizstore } = this.properties;
        const { get, path } = quizstore;
        const { queue } = this;
        const cursor = get(path('cursor'));
        return Boolean(queue && cursor + 1 < queue.length);
    }
    setQuestion() {
        const { quizstore } = this.properties;
        const { get, path } = quizstore;
        const cursor = get(path('cursor'));
        const queue = this.queue;
        const question = queue.elementAtIndex(cursor);
        if (!question.starttime) {
            question.starttime = timesync.now().toJSON();
        }
        setQuestion(quizstore)({ question });
    }
    next_question() {
        const { quizstore, quiz, info } = this.properties;
        if (this.has_next_question()) {
            if (this.cursors.length > 0) {
                this.loc += 1;
                const cursor = this.cursors[this.loc];
                setCursor(quizstore)({ cursor });
                recordProgress(quiz, info, cursor);
                this.setQuestion();
            }
            else {
                incrCursor(quizstore);
                const cursor = quizstore.get(quizstore.path('cursor'));
                recordProgress(quiz, info, cursor);
                this.setQuestion();
            }
        }
    }
    has_previous_question() {
        if (this.cursors.length > 0) {
            return this.loc > 0;
        }
        const { quizstore } = this.properties;
        const { get, path } = quizstore;
        const cursor = get(path('cursor'));
        return cursor > 0;
    }
    previous_question() {
        const { quizstore } = this.properties;
        if (this.has_previous_question()) {
            if (this.cursors.length > 0) {
                this.loc -= 1;
                const cursor = this.cursors[this.loc];
                setCursor(quizstore)({ cursor });
                this.setQuestion();
            }
            else {
                decrCursor(quizstore);
                this.setQuestion();
            }
        }
    }
    show_review(flag) {
        const { quizstore } = this.properties;
        this.inreview = flag;
        if (flag) {
            this.hasReviewed = true;
        }
        setInreview(quizstore)({ inreview: flag });
        logQuiz.info(() => `${flag ? 'show' : 'hide'} review`);
        this.invalidate();
    }
    finish_quiz() {
        const { finalize, svc, isaudit, quizstore } = this.properties;
        setQuestion(quizstore)({ question: undefined });
        if (isaudit) {
            finalize();
        }
        else {
            svc.finish_quiz().then(() => {
                finalize();
            });
        }
    }
    update_results(results) {
        const { info: { qrid }, quiz: { id }, svc, isaudit } = this.properties;
        if (isaudit) {
            return Promise.resolve(true);
        }
        results.forEach((result) => {
            result.qrlocator.qrid = qrid;
            result.qrlocator.quiz = id;
        });
        return svc.update_results({ results });
    }
    filter_on(name) {
        const { quizstore } = this.properties;
        if (isNumber(name)) {
            this.cursors = [];
            setCursor(quizstore)({ cursor: name });
            this.setQuestion();
            return;
        }
        if (name === 'all') {
            this.cursors = [];
            setCursor(quizstore)({ cursor: 0 });
            this.setQuestion();
            return;
        }
        const cursors = [];
        this.queue.forEach((q) => {
            switch (name) {
                case 'marked':
                    if (q.marked) {
                        cursors.push(q.cursor);
                    }
                    break;
                case 'complete':
                    if (q.flags[ISCOMPLETE]) {
                        cursors.push(q.cursor);
                    }
                    break;
                case 'incomplete':
                    if (!q.flags[ISCOMPLETE]) {
                        cursors.push(q.cursor);
                    }
                    break;
            }
        });
        if (cursors.length > 0) {
            setCursor(quizstore)({ cursor: cursors[0] });
            this.loc = 0;
            this.cursors = cursors;
            this.setQuestion();
        }
    }
    check_for(name) {
        if (name === 'all') {
            return true;
        }
        const { queue = dummyList } = this;
        let flag = false;
        queue.forEach((q) => {
            switch (name) {
                case 'marked':
                    flag = flag || !!q.marked;
                    break;
                case 'complete':
                    flag = flag || q.flags[ISCOMPLETE];
                    break;
                case 'incomplete':
                    flag = flag || !q.flags[ISCOMPLETE];
                    break;
            }
        });
        return flag;
    }
    onAttach() {
        const { quizstore } = this.properties;
        if (allqueues.size > 0) {
            logQuiz.debug(() => `already have QuestionList ${Array.from(allqueues.keys()).join(', ')}`);
            this.originalId = this.instanceId;
            this.instanceId = '--bad--';
            this.detaching = true;
            return;
        }
        setHasQueue(quizstore)({ hasqueue: true });
        const { startquiz, instancekey, isaudit } = this.properties;
        if (this.instanceId && this.instanceId !== instancekey) {
            logQuiz.debug(() => `QuestionList#onAttach for defunct ${this.originalId}`);
            return;
        }
        this.instanceId = instancekey;
        this.originalId = instancekey;
        logQuiz.debug(() => `QuestionList#onAttach for ${this.instanceId}`);
        const { loader } = this;
        if (loader) {
            loader.load_questions(startquiz, isaudit);
        }
    }
    render() {
        const { instancekey, quizstore } = this.properties;
        const { detaching, instanceId } = this;
        if (detaching || (instanceId && instanceId !== instancekey)) {
            logQuiz.debug(() => `QuestionList#render defunct instance ${this.originalId}`);
            return null;
        }
        const { isaudit } = this.properties;
        const { question } = this.properties;
        if (!question) {
            return v('div', { key: 'spinner', classes: [css.loading] }, [
                v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })
            ]);
        }
        const { questiontype } = question;
        const key = `${questiontype}-${instancekey}`;
        return w(questiontype, {
            key,
            quizstore,
            inreview: this.inreview,
            hasReviewed: this.hasReviewed,
            isaudit,
            hasNextQuestion: this.has_next_question,
            onNextQuestion: this.next_question,
            hasPreviousQuestion: this.has_previous_question,
            onPreviousQuestion: this.previous_question,
            onFinish: this.finish_quiz,
            updater: this.update_results,
            onReview: this.show_review,
            getQuestions: this.get_question_array,
            filterOn: this.filter_on,
            checkFor: this.check_for
        });
    }
    stateProperties(properties) {
        const { quizstore } = properties;
        const { get, path } = quizstore;
        if (!this._propsInited) {
            const paths = [['quiz'], ['info'], ['question'], ['isaudit'], ['instancekey']];
            const invalidatePaths = paths.map((path) => quizstore.path(path.join('/')));
            const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
            this.own({
                destroy: () => {
                    handle.remove();
                }
            });
            this._propsInited = true;
        }
        properties.quiz = get(path('quiz'));
        properties.info = get(path('info'));
        properties.isaudit = get(path('isaudit'));
        properties.question = get(path('question'));
        properties.instancekey = get(path('instancekey'));
        return properties;
    }
}
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], QuestionList.prototype, "stateProperties", null);
export default QuestionList;
